import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Check } from 'lucide-react';
import { DndContext, PointerSensor, KeyboardSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import useTrainingPlanStore from '../../stores/trainingPlanStore';
import { useTheme } from '../../context/ThemeContext';
import SetRow from './SetRow';
import DrawerBinderTabs from './DrawerBinderTabs';

const DRAWER_WIDTH = 'w-full sm:w-[640px] md:w-[720px]';
const EMPTY_IDS = [];

export default function ExerciseSettingsDrawer() {
 const { themeClasses } = useTheme();
 const exerciseId = useTrainingPlanStore(state => state.ui.expandedExerciseId);
 const drawerLeaf = useTrainingPlanStore(state => state.ui.drawerLeaf);
 const exercise = useTrainingPlanStore(state => (exerciseId ? state.exercises[exerciseId] : null));
 const exerciseIds = useTrainingPlanStore(state => state.workouts[state.ui.editingWorkoutId]?.exerciseIds ?? EMPTY_IDS);
 const exercises = useTrainingPlanStore(state => state.exercises);
 const closeExerciseSettings = useTrainingPlanStore(state => state.closeExerciseSettings);
 const showWorkoutLeaf = useTrainingPlanStore(state => state.showWorkoutLeaf);
 const openExerciseSettings = useTrainingPlanStore(state => state.openExerciseSettings);
 const updateExercise = useTrainingPlanStore(state => state.updateExercise);
 const addSet = useTrainingPlanStore(state => state.addSet);
 const reorderSets = useTrainingPlanStore(state => state.reorderSets);

 const setSensors = useSensors(
  useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
 );

 const language = localStorage.getItem('language') || 'english';
 const he = language === 'hebrew';
 const direction = he ? 'rtl' : 'ltr';

 const [localNotes, setLocalNotes] = useState('');
 const [localRest, setLocalRest] = useState('');

 useEffect(() => {
 if (exercise) {
 setLocalNotes(exercise.coach_notes || '');
 setLocalRest(exercise.rest_seconds ?? '');
 }
 }, [exerciseId, exercise?.coach_notes, exercise?.rest_seconds]);

 const commitFields = () => {
  if (!exercise) return;
  const parsed = parseInt(localRest, 10) || 0;
  const patch = {};
  if (parsed !== exercise.rest_seconds) patch.rest_seconds = parsed;
  if (localNotes !== exercise.coach_notes) patch.coach_notes = localNotes;
  if (Object.keys(patch).length) updateExercise(exerciseId, patch);
 };

 const commitAndClose = () => {
  commitFields();
  closeExerciseSettings();
 };

 const flipToWorkout = () => {
  commitFields();
  showWorkoutLeaf();
 };

 const isFront = drawerLeaf === 'exercise' && !!exerciseId && !!exercise;

 return (
 <AnimatePresence>
 {isFront && (
 <>
              <motion.div
                initial={{ x: he ? '-100%' : '100%' }}
                animate={{ x: 0 }}
                exit={{ x: he ? '-100%' : '100%' }}
                transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                className={`fixed inset-y-0 ${he ? 'left-0 border-r' : 'right-0 border-l'} z-[56] ${DRAWER_WIDTH} flex flex-col bg-surface dark:bg-surface-dark border-line dark:border-line-dark overflow-visible`}
                dir={direction}
              >
                <DrawerBinderTabs
                  active={exerciseId}
                  exercises={exerciseIds.map((id) => ({
                    id,
                    label: exercises[id]?.exercise_name || (he ? 'תרגיל' : 'Exercise'),
                  }))}
                  onWorkout={flipToWorkout}
                  onExercise={(id) => {
                    commitFields();
                    openExerciseSettings(id);
                  }}
                  rtl={he}
                />
                <div className="px-5 py-4 border-b border-line dark:border-line-dark bg-surface dark:bg-surface-dark flex items-start justify-between gap-3">
 <div className="min-w-0">
 <p className="text-[10px] font-bold uppercase tracking-widest text-ink-muted">
 {he ? 'הגדרות תרגיל' : 'Exercise settings'}
 </p>
 <h2 className={`mt-1 text-xl font-semibold tracking-tight truncate ${themeClasses.textPrimary}`}>
 {exercise.exercise_name || (he ? 'תרגיל' : 'Exercise')}
 </h2>
 </div>
                  <button
                    type="button"
                    onClick={commitAndClose}
                    className="min-h-11 min-w-11 inline-flex items-center justify-center rounded-full hover:bg-canvas dark:hover:bg-canvas-dark transition-colors outline-none focus-visible:ring-2 focus-visible:ring-forest"
                    aria-label={he ? 'סגור' : 'Close'}
                  >
                    <X size={18} />
                  </button>
                </div>

 <div className="flex-1 overflow-y-auto p-5 space-y-5 custom-scrollbar">
 <div className="flex items-end gap-3">
 <div className="w-[5.5rem] shrink-0 space-y-1.5">
 <label htmlFor="exercise-rest" className="block text-[11px] font-semibold text-ink-muted">
 {he ? 'מנוחה' : 'Rest'}
 </label>
                      <div className="flex items-center min-h-11 rounded-md border border-line dark:border-line-dark bg-canvas dark:bg-canvas-dark focus-within:border-forest focus-within:ring-2 focus-within:ring-forest">
                      <input
                        id="exercise-rest"
                        type="number"
                        inputMode="numeric"
                        min={0}
                        value={localRest}
                        onChange={(e) => setLocalRest(e.target.value)}
                        onBlur={() => {
                          const parsed = parseInt(localRest, 10) || 0;
                          if (parsed !== exercise.rest_seconds) updateExercise(exerciseId, { rest_seconds: parsed });
                        }}
                        onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                        aria-label={he ? 'מנוחה בשניות' : 'Rest in seconds'}
                        className="w-full min-w-0 min-h-11 ps-2 pe-0 text-center bg-transparent border-0 text-[13px] font-semibold tabular-nums outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <span className="pe-2 text-[11px] text-ink-muted shrink-0">{he ? 'שנ׳' : 's'}</span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <label htmlFor="exercise-notes" className="block text-[11px] font-semibold text-ink-muted">
                        {he ? 'הערות מאמן' : 'Coach notes'}
                      </label>
                      <input
                        id="exercise-notes"
                        type="text"
                        value={localNotes}
                        onChange={(e) => setLocalNotes(e.target.value)}
                        onBlur={() => {
                          if (localNotes !== exercise.coach_notes) updateExercise(exerciseId, { coach_notes: localNotes });
                        }}
                        onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                        placeholder={he ? 'למשל דגש על אקסנטרי איטי' : 'e.g. Slow eccentric'}
                        className="w-full min-h-11 px-3 rounded-md border border-line dark:border-line-dark bg-canvas dark:bg-canvas-dark text-[13px] outline-none focus:border-forest focus:ring-2 focus:ring-forest placeholder:text-ink-muted"
                      />
 </div>
 </div>

 <div className="space-y-2">
 <div className="hidden sm:grid sm:grid-cols-[44px_minmax(0,1fr)_minmax(8.5rem,1.1fr)_72px_96px] gap-2 px-1 text-[11px] font-semibold text-ink-muted">
 <div className="text-center">#</div>
 <div>{he ? 'סוג' : 'Type'}</div>
 <div className="text-center">{he ? 'חזרות' : 'Reps'}</div>
 <div className="text-center">{he ? 'משקל' : 'Weight'}</div>
 <div />
 </div>

 <div className="flex flex-col gap-1.5">
 {(exercise.setIds || EMPTY_IDS).length === 0 ? (
 <div className="rounded-md border border-dashed border-line px-4 py-6 text-center text-sm text-ink-muted">
 {he ? 'אין סטים עדיין' : 'No sets yet'}
 </div>
 ) : (
 <DndContext
  sensors={setSensors}
  collisionDetection={closestCenter}
  onDragEnd={({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = exercise.setIds || EMPTY_IDS;
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    reorderSets(exerciseId, oldIndex, newIndex);
  }}
 >
  <SortableContext items={exercise.setIds} strategy={verticalListSortingStrategy}>
    {exercise.setIds.map((setId) => (
      <SetRow key={setId} setId={setId} exerciseId={exerciseId} />
    ))}
  </SortableContext>
 </DndContext>
 )}
 </div>

 <div className="pt-1">
 <button
 type="button"
 onClick={() => addSet(exerciseId)}
 className="w-full min-h-11 inline-flex items-center justify-center gap-2 rounded-md border border-line dark:border-line-dark bg-canvas dark:bg-canvas-dark text-sm font-semibold hover:border-forest transition-colors"
 >
 <Plus size={16} strokeWidth={2.5} />
 {he ? 'הוסף סט' : 'Add Set'}
 </button>
 </div>
 </div>
 </div>

 <div className="p-4 border-t border-line dark:border-line-dark bg-surface dark:bg-surface-dark">
 <button
 type="button"
 onClick={flipToWorkout}
 className="w-full min-h-12 inline-flex items-center justify-center gap-2 rounded-md bg-forest hover:bg-forest-hover text-white font-bold transition-colors"
 >
 <Check size={18} strokeWidth={2.5} />
 {he ? 'שמור תרגיל' : 'Save exercise'}
 </button>
 </div>
 </motion.div>
 </>
 )}
 </AnimatePresence>
 );
}
